/**
 * Register Import Wizard — rail summary (WizardShell #sidebar-extras). Pure functions: the step's
 * summary box, and the info rows (Facility / Facility Group scope, register mode, saved schema).
 */
import { REGISTER_KIND, isGroupRegister, resultingMode } from '../../../modules/registerImportWizardModel.js';

const fmt = (value) => Number(value || 0).toLocaleString('en-US');

/**
 * @param {object} state - { isImport, step, fileSummary, warningCount, mappingStatus }
 * @returns {?{title:string, rows:Array<{label:string, value:string, tone?:string}>}}
 */
export function importRailStats({ isImport, step, fileSummary, warningCount, mappingStatus }) {
  if (isImport && step === 1 && fileSummary) {
    const rows = [
      { label: 'Rows found', value: fmt(fileSummary.rows), tone: 'hl' },
      { label: 'Columns', value: fmt(fileSummary.columns.length) },
    ];
    if (fileSummary.sheet) rows.push({ label: 'Sheet', value: fileSummary.sheet });
    if (warningCount) rows.push({ label: 'Warnings', value: fmt(warningCount), tone: 'wn' });
    return { title: 'File Summary', rows };
  }

  const onMapping = isImport ? step === 2 : step === 1;
  const s = mappingStatus;
  if (!onMapping || !s) return null;

  const rows = [{ label: 'Fields configured', value: `${s.configured} / ${s.totalColumns}`, tone: s.configured ? 'hl' : '' }];
  if (isImport) {
    rows.push({ label: 'Tag column', value: s.tagColumn || 'Not set', tone: s.tagColumn ? '' : 'muted' });
    const state = s.facilityColumnState;
    rows.push(s.facilityColumn
      ? { label: 'Facility column', value: s.facilityColumn }
      : { label: 'Facility column', value: state ? state.value : 'Not set', tone: state ? state.tone : 'muted' });
  }
  rows.push({ label: 'Identity', value: s.identity || 'Not set', tone: s.identity ? '' : (s.configured && isImport ? 'wn' : 'muted') });
  if (isImport || s.linked) rows.push({ label: 'Linked columns', value: fmt(s.linked) });
  return { title: isImport ? 'Mapping' : 'Schema', rows };
}

/**
 * @param {object} state - { register, isImport, registerKind, facilityGroupName, hasFacilityColumn, savedSchemaFieldCount }
 * @returns {Array<{icon:string, text:string, title?:string}>}
 */
export function importRailInfoRows({ register, isImport, registerKind, facilityGroupName, hasFacilityColumn, savedSchemaFieldCount }) {
  const reg = register || {};
  const rows = [];
  if (isGroupRegister(reg)) {
    rows.push({ icon: 'mdi-office-building-marker-outline', text: facilityGroupName ? `Facility Group · ${facilityGroupName}` : 'Facility Group register' });
  } else {
    const facilityName = reg.facility && reg.facility.name;
    rows.push({ icon: 'mdi-domain', text: facilityName ? `Facility · ${facilityName}` : 'Facility register' });
  }

  if (isImport && isGroupRegister(reg)) {
    if (registerKind === REGISTER_KIND.GROUP_UNSET) {
      rows.push({ icon: 'mdi-lock-alert-outline', text: 'Mode not set', title: 'Has rows but no Shared/Separated mode' });
    } else {
      const mode = resultingMode(registerKind, hasFacilityColumn);
      const icon = mode === 'Separated' ? 'mdi-call-split' : 'mdi-share-variant-outline';
      rows.push(registerKind === REGISTER_KIND.GROUP_EMPTY
        ? { icon, text: `${mode} · set by this import`, title: 'Empty register — the first import sets the mode' }
        : { icon, text: `${mode} register`, title: mode === 'Separated' ? 'Each Facility sees only its rows' : 'Every Facility in the Facility Group sees all rows' });
    }
  }

  if (savedSchemaFieldCount) {
    rows.push({ icon: 'mdi-table-check', text: `Saved schema · ${savedSchemaFieldCount} ${savedSchemaFieldCount === 1 ? 'field' : 'fields'}` });
  }
  return rows;
}

/**
 * WizardShell steps (rail title/description + pane heading/subheading) for import and schema update.
 * @param {object} state - { isImport, registerName, fileName, previewing, importing }
 */
export function importWizardSteps({ isImport, registerName, fileName, previewing, importing }) {
  if (!isImport) {
    return [
      { title: 'Configure', description: 'Field names and links', heading: 'Configure Register Schema', subheading: 'Rename fields and set what their values link to. Only names and links can change.' },
      { title: 'Complete', description: 'Schema updated', heading: 'Schema Updated', subheading: registerName },
    ];
  }
  return [
    { title: 'Upload File', description: 'Select your Excel file', heading: 'Upload Excel File', subheading: 'Select the file with the rows to import into this register' },
    {
      title: 'Map Fields',
      description: 'Match columns to fields',
      heading: 'Map Fields',
      subheading: previewing
        ? 'Preview — every row with the current mapping applied'
        : 'Name the register field for each Excel column, then set its type and role',
    },
    {
      title: 'Complete',
      description: 'Import results',
      heading: importing ? 'Importing' : 'Import Complete',
      subheading: importing ? `Writing rows to ${registerName}` : [registerName, fileName].filter(Boolean).join(' · '),
    },
  ];
}
